import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  Pagination,
  Tooltip,
  TextField,
  useMediaQuery, // Import useMediaQuery
  useTheme, // Import useTheme
} from "@mui/material";
import Navbar from "../components/Navbar";
import PackingTable from "../components/PackingTable";
import CustomSelect from "../components/CustomSelect";
import colors from "../theme/Colors";
import AddIcon from '@mui/icons-material/Add';
import { api } from "../helpers/apiRequest";
import AddPackingListModal from '../components/AddPackingListModal';
import { toast } from "react-toastify";
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { pageAccessList } from "../helpers/pageAccessList";


function PackingList() {
  const [userInfo] = useState(JSON.parse(localStorage.getItem('user')));
  const [parties, setParties] = useState([]);
  const [filters, setFilters] = useState({
    party: 'all',
  });
  const [openModal, setOpenModal] = useState(false);
  const [refreshTable, setRefreshTable] = useState(false);
  const [selectedPacking, setSelectedPacking] = useState(null);
  const [packingData, setPackingData] = useState([]);
  const [editingPacking, setEditingPacking] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [totalPages, setTotalPages] = useState(1);
  const [lotFilter, setLotFilter] = useState('');
  const [containerFilter, setContainerFilter] = useState('');
  const [exFactoryFilter, setExFactoryFilter] = useState(null);
  const [shipDateFilter, setShipDateFilter] = useState(null);
  const [arrivalFilter, setArrivalFilter] = useState(null);

  // Refs for filters for useEffect dependencies
  const rppRef = useRef(rowsPerPage);
  const filtersRef = useRef(filters);
  const lotFilterRef = useRef(lotFilter);
  const containerFilterRef = useRef(containerFilter);
  const exFactoryFilterRef = useRef(exFactoryFilter);
  const shipDateFilterRef = useRef(shipDateFilter);
  const arrivalFilterRef = useRef(arrivalFilter);

  // Material-UI hooks for responsiveness
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm')); // Less than 600px
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md')); // Less than 900px

  useEffect(() => { lotFilterRef.current = lotFilter; }, [lotFilter]);
  useEffect(() => { containerFilterRef.current = containerFilter; }, [containerFilter]);
  useEffect(() => { exFactoryFilterRef.current = exFactoryFilter; }, [exFactoryFilter]);
  useEffect(() => { shipDateFilterRef.current = shipDateFilter; }, [shipDateFilter]);
  useEffect(() => { arrivalFilterRef.current = arrivalFilter; }, [arrivalFilter]);
  useEffect(() => { filtersRef.current = filters; }, [filters]);
  useEffect(() => { rppRef.current = rowsPerPage; }, [rowsPerPage]);

  const [accessControl, setAccessControl] = useState({});
  const fetchCurrentUser = async () => {
    try {
      let user = await localStorage.getItem('user');
      user = JSON.parse(user);

      if (user) {
        const res = await api.get(`/users/get/routes?userId=${userInfo?.id}`);
        const accessArray = res.data.message || [];
        const accessMapFromServer = accessArray.reduce((map, item) => {
          map[item.route] = item.allowed;
          return map;
        }, {});

        const accessMap = pageAccessList.reduce((acc, page) => {
          acc[page.key] = accessMapFromServer[page.key] || false;
          return acc;
        }, {});
        setAccessControl(accessMap);
      }
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  const handleReceiveStock = async (packingListId) => {
    try {
      await api.post('/inventory/packing-list/receive', { packingListId });
      toast.success('Stock received successfully!');
      setSelectedPacking(null);
      setRefreshTable(prev => !prev); // Toggle refreshTable to trigger refetch
    } catch (error) {
      toast.error(error?.response?.data || 'Failed to receive stock.');
      console.error(error);
    }
  };

  const handleReceiveStockAndPlaceOrder = async (packingListId) => {
    try {
      await api.post('/inventory/packing-list/receiveNPlaceOrder', { packingListId });
      toast.success('Stock received and order placed successfully!');
      setSelectedPacking(null);
      setRefreshTable(prev => !prev); // Toggle refreshTable to trigger refetch
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to process stock and order.');
      console.error(error);
    }
  };

  const handleDeletePackingList = async (id) => {
    try {
      await api.delete(`/inventory/packing-list-delete/${id}`);
      toast.success('Packing list deleted successfully');
      
      fetchPackingData();
      setRefreshTable(prev => !prev);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to delete packing list.');
    }
  };

  const fetchDropdownData = async () => {
    try {
      const endpoint =
        userInfo?.role === 'merchant'
          ? `inventory/party/merchant/${userInfo?.id}`
          : `inventory/party`;

      const [partiesRes] = await Promise.all([api.get(endpoint)]);
      setParties(partiesRes.data.message);
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    }
  };

  const fetchPackingData = async () => { // Removed arguments, using refs directly
    try {
      const endpoint = getApiEndpoint();
      if (!endpoint) return;

      const queryParams = new URLSearchParams({
        page: page + 1,
        limit: rppRef.current,
        ...(filtersRef.current.party !== 'all' && { party: filtersRef.current.party }),
        ...(lotFilterRef.current && { lot: lotFilterRef.current }),
        ...(containerFilterRef.current && { container: containerFilterRef.current }),
        ...(exFactoryFilterRef.current && { ex_factory: exFactoryFilterRef.current?.toISOString().split('T')[0] }),
        ...(shipDateFilterRef.current && { ship_date: shipDateFilterRef.current?.toISOString().split('T')[0] }),
        ...(arrivalFilterRef.current && { arrival: arrivalFilterRef.current?.toISOString().split('T')[0] }),
      });

      const response = await api.get(`${endpoint}?${queryParams}`);
      setPackingData(response.data.data);

      const totalCount = response.data.total_count || response.data.length;
      setTotalPages(Math.ceil(totalCount / rppRef.current));
    } catch (error) {
      console.error('Error fetching packing list:', error);
    }
  };

  useEffect(() => {
    fetchDropdownData();
    // Only fetch packing data if user info is available (role dependent)
    if (userInfo) {
      fetchPackingData();
    }
  }, [page, rowsPerPage, filters.party, refreshTable, lotFilter, containerFilter, exFactoryFilter, shipDateFilter, arrivalFilter, userInfo]); // Add userInfo to deps

  // Auto-refresh interval (moved to its own useEffect for clarity)
  useEffect(() => {
    const interval = setInterval(() => {
      if (userInfo) { // Only refresh if user is logged in
        fetchPackingData();
      }
    }, 10000); // 10 seconds

    return () => clearInterval(interval);
  }, [userInfo]); // Depend on userInfo to start/stop interval

  const getApiEndpoint = () => {
    switch (userInfo?.role) {
      case 'admin':
        return `/inventory/packing-list`;
      case 'customer':
        return `/inventory/packing-list/customer/${userInfo?.id}`;
      case 'merchant':
        return `/inventory/packing-list/merchant/${userInfo?.id}`;
      default:
        return null;
    }
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingPacking(null);
  };

  const handleEditClick = async (packing) => {
    try {
      const response = await api.get(`inventory/packing-list/${packing._id}`);
      console.log("response:",response);
      setEditingPacking(response.data.data);
      setOpenModal(true);
    } catch (error) {
      console.error('Error fetching packing details:', error);
      toast.error("Error loading packing details");
    }
  };

  const handleSubmitPackingList = async (formData) => {
    try {
      let response;
      if (editingPacking) {
        response = await api.put(`inventory/packing-list/${editingPacking._id}`, formData);
        if (response) {
          toast.success("Packing List Updated Successfully");
        }
      } else {
        response = await api.post('inventory/packing-list/add', formData);
        if (response) {
          toast.success("Packing List Added Successfully");
        }
      }
      setRefreshTable(prev => !prev); // Toggle refreshTable
      handleCloseModal();
    } catch (error) {
      console.error('Error with packing list:', error);
      toast.error(error?.response?.data?.message || "Error occurred while processing");
    }
  };

  const handleCloseDetails = () => {
    setSelectedPacking(null);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPage(0); // Reset page on filter change
  };

  return (
    <Box sx={{
      display: "flex",
      flexDirection: "column",
      padding: { xs: 1, sm: 2, md: 3 }, // Responsive padding
      width: '95%', // Take full width
      // Add max-width for content readability on very wide screens, and center it
      maxWidth: '1400px',
      margin: '0 auto',
    }}>
      <Navbar title="Packing List" />

      {/* Filter and Add Button Section */}
      <Box
        sx={{
          marginTop: { xs: 8, sm: 10 }, // Adjust top margin for fixed Navbar on small screens
          marginBottom: { xs: 2, sm: 3 }, // Responsive bottom margin
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' }, // Stack on small, row on medium/large
          alignItems: { xs: 'flex-start', md: 'center' }, // Align items (top for stacked, center for row)
          gap: { xs: 2, sm: 3 }, // Responsive gap between filter row and button
          justifyContent: 'space-between', // Push button to end on large screens
        }}
      >
        {/* Filters Box */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap', // Allow filters to wrap to next line
            gap: { xs: 1.5, sm: 2 }, // Responsive gap between individual filters
            width: { xs: '100%', md: 'auto' }, // Full width on small, auto on medium/large
            justifyContent: { xs: 'center', sm: 'flex-start' } // Center filters on xs, left-align on sm+
          }}
        >
          {userInfo?.role !== 'customer' && (
            <CustomSelect
              label="Filter by Party"
              value={filters.party}
              onChange={(e) => handleFilterChange('party', e.target.value)}
              options={[
                { id: 'all', name: 'All Parties' },
                ...parties.map((p) => ({ id: p._id, name: p.name }))
              ]}
              sx={{
                minWidth: { xs: 'calc(50% - 8px)', sm: 180, md: 200 }, // Two columns on xs, wider on sm+
                flexGrow: 1, // Allow it to grow
              }}
            />
          )}
          <TextField
            label="Filter by Lot"
            value={lotFilter}
            onChange={(e) => setLotFilter(e.target.value)}
            sx={{
              minWidth: { xs: 'calc(50% - 8px)', sm: 180, md: 200 },
              flexGrow: 1,
            }}
            size={isSmallScreen ? "small" : "medium"} // Smaller input on small screens
          />
          <TextField
            label="Filter by Container"
            value={containerFilter}
            onChange={(e) => setContainerFilter(e.target.value)}
            sx={{
              minWidth: { xs: 'calc(50% - 8px)', sm: 180, md: 200 },
              flexGrow: 1,
            }}
            size={isSmallScreen ? "small" : "medium"}
          />
          <TextField
            label="Filter by Ex-Factory"
            type="date"
            value={exFactoryFilter ? exFactoryFilter.toISOString().split('T')[0] : ''}
            onChange={(e) => {
              const value = e.target.value;
              setExFactoryFilter(value ? new Date(value) : null);
            }}
            sx={{
              minWidth: { xs: 'calc(50% - 8px)', sm: 180, md: 200 },
              flexGrow: 1,
            }}
            InputLabelProps={{ shrink: true }}
            size={isSmallScreen ? "small" : "medium"}
          />
          <TextField
            label="Filter by Ship Date"
            type="date"
            value={shipDateFilter ? shipDateFilter.toISOString().split('T')[0] : ''}
            onChange={(e) => {
              const value = e.target.value;
              setShipDateFilter(value ? new Date(value) : null);
            }}
            sx={{
              minWidth: { xs: 'calc(50% - 8px)', sm: 180, md: 200 },
              flexGrow: 1,
            }}
            InputLabelProps={{ shrink: true }}
            size={isSmallScreen ? "small" : "medium"}
          />
          <TextField
            label="Filter by Arrival"
            type="date"
            value={arrivalFilter ? arrivalFilter.toISOString().split('T')[0] : ''}
            onChange={(e) => {
              const value = e.target.value;
              setArrivalFilter(value ? new Date(value) : null);
            }}
            sx={{
              minWidth: { xs: 'calc(50% - 8px)', sm: 180, md: 200 },
              flexGrow: 1,
            }}
            InputLabelProps={{ shrink: true }}
            size={isSmallScreen ? "small" : "medium"}
          />
        </Box>

        {/* Add Packing List Button */}
        {(accessControl["add-packing-list"] === true) ? (
          <Button
            variant="contained"
            sx={{
              backgroundColor: colors.primary,
              color: 'black', // Assuming black text on primary background
              '&:hover': {
                backgroundColor: colors.primary,
                opacity: 0.9
              },
              width: { xs: '100%', md: 'auto' }, // Full width on small, auto on medium/large
              mt: { xs: 2, md: 0 }, // Top margin on small, no top margin on medium/large
              minHeight: { xs: '48px', sm: '56px' } // Ensure button is tappable
            }}
            startIcon={<AddIcon />}
            onClick={handleOpenModal}
          >
            Add Packing List
          </Button>
        ) : null}
      </Box>

      {/* Main Table */}
      <TableContainer component={Paper} sx={{ mt: 2, overflowX: 'auto' }}>
        <Table sx={{ minWidth: 650 }}> {/* Set a minWidth for the table to ensure scrolling */}
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: colors.primary, fontWeight: 'bold' }}>Party</TableCell>
              <TableCell sx={{ backgroundColor: colors.primary, fontWeight: 'bold' }}>Lot</TableCell>
              <TableCell sx={{ backgroundColor: colors.primary, fontWeight: 'bold' }}>Container</TableCell>
              <TableCell sx={{ backgroundColor: colors.primary, fontWeight: 'bold' }}>Ex-Factory</TableCell>
              <TableCell sx={{ backgroundColor: colors.primary, fontWeight: 'bold' }}>Ship Date</TableCell>
              <TableCell sx={{ backgroundColor: colors.primary, fontWeight: 'bold' }}>Arrival</TableCell>
              <TableCell sx={{ backgroundColor: colors.primary, fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ backgroundColor: colors.primary, fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {packingData.length > 0 ? (
              packingData.map((packing) => (
                <TableRow key={packing._id}>
                  <TableCell>{packing.party?.name}</TableCell>
                  <TableCell>{packing.lot}</TableCell>
                  <TableCell>{packing.container}</TableCell>
                  <TableCell>{packing?.ex_factory === null ? 'N/A' : new Date(packing?.ex_factory).toLocaleDateString()}</TableCell>
                  <TableCell>{packing?.ship_date === null ? 'N/A' : new Date(packing?.ship_date).toLocaleDateString()}</TableCell>
                  <TableCell>{packing?.arrival === null ? 'N/A' : new Date(packing?.arrival).toLocaleDateString()}</TableCell>
                  <TableCell>{packing.status}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}> {/* Prevent actions from wrapping */}
                    <Tooltip title="View Details">
                      <IconButton
                        onClick={() => setSelectedPacking(packing)}
                        sx={{ color: colors.primary }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    {((userInfo.role === 'admin' || accessControl["edit-packing-list"] === true) && packing?.status !== 'RECEIVED') && (
                      <Tooltip title="Edit Packing List">
                        <IconButton
                          onClick={() => handleEditClick(packing)}
                          sx={{ color: colors.primary }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {(userInfo.role === 'admin' || accessControl["edit-packing-list"] === true) && (
                      <Tooltip title="Delete Packing List">
                        <IconButton
                          onClick={() => handleDeletePackingList(packing._id)}
                          sx={{ color: 'error.main' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} sx={{ textAlign: 'center', py: 3 }}>
                  No packing lists found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination and Rows per page */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' }, // Stack on small, row on medium/large
          justifyContent: 'center', // Center content
          alignItems: 'center',
          mt: 2,
          mb: 2,
          gap: { xs: 2, sm: 3 }, // Responsive gap
        }}
      >
        <Pagination
          count={totalPages}
          page={page + 1} // Pagination is 1-based, state is 0-based
          onChange={(event, value) => setPage(value - 1)} // Convert back to 0-based
          color="primary"
          showFirstButton
          showLastButton
          size={isSmallScreen ? "small" : "medium"} // Smaller pagination on small screens
        />
        <CustomSelect
          label="Rows per page"
          value={rowsPerPage}
          onChange={handleChangeRowsPerPage}
          options={[
            // { id: 5, name: '5' },
            // { id: 10, name: '10' },
            // { id: 25, name: '25' },
            // { id: 50, name: '50' },
            { id: 100, name: '100' },
                        { id: 200, name: '200' },
                        { id: 300, name: '300' },
                        { id: 400, name: '400' },
                        { id: 500, name: '500' },
          ]}
          sx={{ minWidth: 120 }}
        />
      </Box>

      {/* Packing List Details Dialog */}
      <Dialog
        open={Boolean(selectedPacking)}
        onClose={handleCloseDetails}
        maxWidth="xl" // Max width for the dialog
        fullWidth // Takes full width up to maxWidth
        // Added responsive styles for the dialog paper
        PaperProps={{
          sx: {
            margin: { xs: 1, sm: 2 }, // Smaller margin on small screens
            width: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 32px)' }, // Full width minus margin
            maxHeight: '90vh', // Limit height to prevent overflow
          }
        }}
      >
        <DialogTitle sx={{ backgroundColor: colors.primary, color: 'black', pr: { xs: 5, sm: 8 } }}> {/* Add right padding to prevent close button overlap */}
          Packing List Details
          <IconButton
            onClick={handleCloseDetails}
            sx={{
              position: 'absolute',
              right: { xs: 8, sm: 16 }, // Adjust right position for close button
              top: { xs: 8, sm: 16 }, // Adjust top position for close button
              color: 'black'
            }}
          >
            ×
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 3 }, mt: { xs: 2, sm: 5 }, overflowY: 'auto' }}> {/* Responsive padding and scroll */}
          {selectedPacking && (
            <PackingTable
              handleReceiveStock={handleReceiveStock}
              handleReceiveStockAndPlaceOrder={handleReceiveStockAndPlaceOrder}
              userInfo={userInfo}
              refreshTable={refreshTable}
              setRefreshTable={setRefreshTable}
              packingData={[selectedPacking]}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Add/Edit Packing List Modal (already responsive via MUI Dialog) */}
      <AddPackingListModal
        open={openModal}
        onClose={handleCloseModal}
        onSubmit={handleSubmitPackingList}
        editData={editingPacking}
        merchantId={editingPacking?.merchant?._id}
      />
    </Box>
  );
}

export default PackingList;
